import * as React from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ManualAddDialog } from '@/components/events/event-waitlist-actions'
import { deleteJSON } from '@/lib/api'
import { WaitlistOfferRow } from './waitlist-offer-row'

const offerStatuses = ['waiting', 'offer_sent', 'claimed', 'declined', 'lapsed'] as const
const allStatuses = [...offerStatuses, 'left', 'removed'] as const
type WaitlistStatus = (typeof allStatuses)[number]
type WaitlistSort = 'position' | 'joined'
type SortDirection = 'ascending' | 'descending'

export type WaitlistEntry = {
  id: number
  position: number
  name: string
  email: string
  party_size: number
  joined: string
  status: WaitlistStatus
  offer_expiry: string | null
  ticket_type: { id: number; name: string }
}

type WaitlistBook = {
  waitlist: {
    enabled: boolean
    entries: WaitlistEntry[]
    status_counts: Record<(typeof offerStatuses)[number] | 'all', number>
    ticket_types: { id: number; name: string }[]
  }
}

export function EventWaitlist({ eventId }: { eventId: number }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = React.useState('')
  const [ticketTypeId, setTicketTypeId] = React.useState('all')
  const [status, setStatus] = React.useState<WaitlistStatus | 'all'>('all')
  const [sort, setSort] = React.useState<WaitlistSort>('position')
  const [direction, setDirection] = React.useState<SortDirection>('ascending')
  const query = useQuery({
    queryKey: ['event-waitlist', eventId, search, ticketTypeId, status, sort, direction],
    queryFn: () =>
      getJSON<WaitlistBook>(
        waitlistPath(eventId, { search, ticketTypeId, status, sort, direction }),
      ),
    placeholderData: (previous) => previous,
    retry: false,
  })
  const remove = useMutation({
    mutationFn: (entryId: number) =>
      deleteJSON(`/api/v1/events/${eventId}/waitlist_entries/${entryId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-waitlist', eventId] }),
  })

  if (query.isLoading) return <WaitlistMessage>Loading waitlist…</WaitlistMessage>
  if (query.isError) return <WaitlistMessage>Waitlist could not be loaded.</WaitlistMessage>
  if (!query.data?.waitlist.enabled)
    return (
      <EmptyState
        title="The waitlist is off for this event."
        description="Turn on “Waitlist when full” in the event editor and full Ticket Types will start collecting joins."
        action={
          <Button asChild variant="outline">
            <a href={`/events/manage/${eventId}?tab=settings`}>Open event editor</a>
          </Button>
        }
      />
    )

  return (
    <div className="space-y-3">
      <WaitlistToolbar
        eventId={eventId}
        search={search}
        ticketTypeId={ticketTypeId}
        status={status}
        ticketTypes={query.data.waitlist.ticket_types}
        onSearch={setSearch}
        onTicketType={setTicketTypeId}
        onStatus={setStatus}
        onAdded={() => queryClient.invalidateQueries({ queryKey: ['event-waitlist', eventId] })}
      />
      <StatusChips
        counts={query.data.waitlist.status_counts}
        selected={status}
        onSelect={setStatus}
      />
      <WaitlistTable
        query={query}
        eventId={eventId}
        onOffer={() => queryClient.invalidateQueries({ queryKey: ['event-waitlist', eventId] })}
        sort={sort}
        direction={direction}
        onSort={(nextSort) => {
          if (nextSort === sort)
            setDirection((current) => (current === 'ascending' ? 'descending' : 'ascending'))
          else {
            setSort(nextSort)
            setDirection('ascending')
          }
        }}
        onRemove={(entryId) => remove.mutate(entryId)}
      />
    </div>
  )
}

function WaitlistToolbar({
  eventId,
  search,
  ticketTypeId,
  status,
  ticketTypes,
  onSearch,
  onTicketType,
  onStatus,
  onAdded,
}: {
  eventId: number
  search: string
  ticketTypeId: string
  status: WaitlistStatus | 'all'
  ticketTypes: { id: number; name: string }[]
  onSearch: (value: string) => void
  onTicketType: (value: string) => void
  onStatus: (value: WaitlistStatus | 'all') => void
  onAdded: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative w-full max-w-xs">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          className="h-8 pl-8 text-caption"
          placeholder="Search name or email"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
      </div>
      <Select value={ticketTypeId} onValueChange={onTicketType}>
        <SelectTrigger aria-label="Ticket type" className="h-8 w-48 text-caption">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Ticket type · All</SelectItem>
          {ticketTypes.map((ticketType) => (
            <SelectItem key={ticketType.id} value={String(ticketType.id)}>
              {ticketType.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={status} onValueChange={onStatus}>
        <SelectTrigger aria-label="Status" className="h-8 w-40 text-caption">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Status · All</SelectItem>
          {allStatuses.map((value) => (
            <SelectItem key={value} value={value}>
              {statusLabel(value)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <a
        href={`/events/manage/${eventId}?tab=settings`}
        className="ml-auto text-caption font-medium text-primary hover:underline"
      >
        Event settings
      </a>
      <ManualAddDialog eventId={eventId} ticketTypes={ticketTypes} onAdded={onAdded} />
    </div>
  )
}

function StatusChips({
  counts,
  selected,
  onSelect,
}: {
  counts: WaitlistBook['waitlist']['status_counts']
  selected: WaitlistStatus | 'all'
  onSelect: (value: WaitlistStatus | 'all') => void
}) {
  const filters: ((typeof offerStatuses)[number] | 'all')[] = ['all', ...offerStatuses]
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {filters.map((value) => {
        const count = counts[value]
        return (
          <Button
            key={value}
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={selected === value}
            className={cn(
              'h-7 gap-1.5 px-2.5 text-caption font-normal',
              selected === value && 'border-primary/40 bg-primary/5 text-foreground',
            )}
            onClick={() => onSelect(value)}
          >
            {statusLabel(value)} <span className="text-muted-foreground tabular-nums">{count}</span>
          </Button>
        )
      })}
    </div>
  )
}

function WaitlistTable({
  query,
  eventId,
  onOffer,
  sort,
  direction,
  onSort,
  onRemove,
}: {
  query: UseQueryResult<WaitlistBook>
  eventId: number
  onOffer: () => void
  sort: WaitlistSort
  direction: SortDirection
  onSort: (sort: WaitlistSort) => void
  onRemove: (entryId: number) => void
}) {
  const entries = query.data?.waitlist.entries ?? []
  if (entries.length === 0)
    return (
      <EmptyState
        variant="quiet"
        title="No one is waiting."
        description="When a Ticket Type sells out, Waitlist joins appear here in queue order."
      />
    )

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortHeader
                label="Position"
                active={sort === 'position'}
                direction={direction}
                onClick={() => onSort('position')}
              />
            </TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Party size</TableHead>
            <TableHead>
              <SortHeader
                label="Joined"
                active={sort === 'joined'}
                direction={direction}
                onClick={() => onSort('joined')}
              />
            </TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <WaitlistOfferRow
              eventId={eventId}
              entry={entry}
              onOffer={onOffer}
              onRemove={onRemove}
              key={entry.id}
              className={entry.status === 'lapsed' ? 'text-muted-foreground' : undefined}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function SortHeader({
  label,
  active,
  direction,
  onClick,
}: {
  label: string
  active: boolean
  direction: SortDirection
  onClick: () => void
}) {
  const Icon = active ? (direction === 'ascending' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 text-caption font-normal uppercase tracking-wide text-muted-foreground hover:text-foreground',
        active && 'text-foreground',
      )}
      onClick={onClick}
    >
      {label}
      <Icon className="size-3" strokeWidth={1.5} aria-hidden="true" />
    </button>
  )
}

export function WaitlistStatus({ entry }: { entry: WaitlistEntry }) {
  const variant =
    entry.status === 'offer_sent' ? 'warning' : entry.status === 'claimed' ? 'success' : 'neutral'
  return (
    <div className="space-y-0.5">
      <Badge variant={variant}>{statusLabel(entry.status)}</Badge>
      {entry.offer_expiry && (
        <p className="text-caption text-muted-foreground">{entry.offer_expiry}</p>
      )}
    </div>
  )
}

function WaitlistMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border px-4 py-12 text-center text-caption text-muted-foreground">
      {children}
    </div>
  )
}

function waitlistPath(
  eventId: number,
  filters: {
    search: string
    ticketTypeId: string
    status: WaitlistStatus | 'all'
    sort: WaitlistSort
    direction: SortDirection
  },
) {
  const params = new URLSearchParams()
  if (filters.search.trim()) params.set('search', filters.search.trim())
  if (filters.ticketTypeId !== 'all') params.set('ticket_type_id', filters.ticketTypeId)
  if (filters.status !== 'all') params.set('status', filters.status)
  params.set('sort', filters.sort)
  params.set('direction', filters.direction)
  return `/api/v1/events/${eventId}/waitlist_entries?${params}`
}

function statusLabel(status: WaitlistStatus | 'all') {
  const labels: Record<WaitlistStatus | 'all', string> = {
    all: 'All',
    waiting: 'Waiting',
    offer_sent: 'Offer sent',
    claimed: 'Claimed',
    declined: 'Declined',
    lapsed: 'Lapsed',
    left: 'Left',
    removed: 'Removed',
  }
  return labels[status]
}
